import React, { useEffect, useState } from "react";
import "../../assets/css/reportRegistrations.css";

const initialFormData = {
  reportId: "",
  groupId: "",
  datasetId: "",
  rlsEnabled: "false",
  reportName: "",
  reportUrl: "",
  reportDescription: "",
};

const ReportRegistrationsFrom = ({ editReportData, onClearEdit }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const reportRegistrationEndpoint = `${process.env.REACT_APP_SERVER_URL}/aipd/report-management/reports`;

  const passtoken =
    !localStorage.getItem("access-token-storage") ||
    localStorage.getItem("access-token-storage") === null
      ? null
      : JSON.parse(localStorage.getItem("access-token-storage"))["accessToken"];

  const showToast = (message, type = "success") => {
    setToastState({
      show: true,
      type,
      message,
    });
  };

  useEffect(() => {
    if (!editReportData) {
      return;
    }

    const prefilledData = {
      ...initialFormData,
      ...editReportData,
      reportUrl:
        editReportData.reportId && editReportData.groupId
          ? `https://app.powerbi.com/reportEmbed?reportId=${editReportData.reportId}&groupId=${editReportData.groupId}`
          : "",
      rlsEnabled:
        editReportData.rlsEnabled === true || editReportData.rlsEnabled === "true"
          ? "true"
          : "false",
    };

    setFormData(prefilledData);
  }, [editReportData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      const generatedUrl =
        updatedData.reportId && updatedData.groupId
          ? `https://app.powerbi.com/reportEmbed?reportId=${updatedData.reportId}&groupId=${updatedData.groupId}`
          : "";
      return { ...updatedData, reportUrl: generatedUrl };
    });
  };

  useEffect(() => {
    if (!toastState.show) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setToastState((prevState) => ({ ...prevState, show: false }));
    }, 3000);

    return () => clearTimeout(timer);
  }, [toastState.show]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      { key: "reportName", label: "Report Name" },
      { key: "reportId", label: "Report ID" },
      { key: "groupId", label: "Group ID" },
      { key: "datasetId", label: "Dataset ID" },
      { key: "reportUrl", label: "Report URL" },
    ];

    const emptyField = requiredFields.find(
      (field) => !String(formData[field.key] || "").trim()
    );

    if (emptyField) {
      showToast(`${emptyField.label} cannot be empty.`, "danger");
      return;
    }

    const payload = {
      reportName: formData.reportName.trim(),
      reportGuid: formData.reportId.trim(),
      groupID: formData.groupId.trim(),
      datasetId: formData.datasetId.trim(),
      rls: formData.rlsEnabled === "true",
      reportURL: formData.reportUrl.trim(),
      reportDescriptions: formData.reportDescription.trim(),
    };

    setIsSubmitting(true);

    try {
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };

      if (passtoken) {
        headers["oidc-token"] = passtoken;
      }

      const response = await fetch(reportRegistrationEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const responseJson = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(responseJson?.message || "Failed to register report.");
      }

      showToast("Report registered successfully.", "success");
      handleReset();
    } catch (error) {
      showToast(error?.message || "Unable to register report at the moment.", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    if (onClearEdit) {
      onClearEdit();
    }
  };

  return (
    <div className="rr-wrap container-fluid pb-4">
      {toastState.show && (
        <div
          style={{
            position: "fixed",
            top: "16px",
            right: "16px",
            zIndex: 99999,
            minWidth: "320px",
            maxWidth: "420px",
          }}
        >
          <div className={`alert alert-${toastState.type} shadow-sm mb-0`} role="alert">
            {toastState.message}
          </div>
        </div>
      )}

      <div className="rr-card">
        <div className="rr-cardHeader">
          <p className="rr-kicker">Metadata Intake</p>
          <h5 className="rr-title">
            {editReportData ? "Update Power BI Report" : "Register Power BI Report"}
          </h5>
          <p className="rr-subtitle">
            Capture report identity, workspace mapping, and access settings.
          </p>
        </div>

        <div className="rr-content">
          <div className="rr-sidePanel">
            <div className="rr-sideBadge">Checklist</div>
            <ul className="rr-checklist">
              <li>Use valid GUID values for Report and Dataset IDs.</li>
              <li>Group ID should match the Power BI workspace.</li>
              <li>Enable RLS only when dataset has role definitions.</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-3 rr-formGrid">
              <div className="col-md-6">
                <label htmlFor="reportName" className="form-label rr-label">
                  Report Name
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  id="reportName"
                  name="reportName"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter report name"
                  value={formData.reportName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="reportId" className="form-label rr-label">
                  Report ID
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  id="reportId"
                  name="reportId"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter report id"
                  value={formData.reportId}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="groupId" className="form-label rr-label">
                  Group ID
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  id="groupId"
                  name="groupId"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter group id"
                  value={formData.groupId}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="datasetId" className="form-label rr-label">
                  Dataset ID
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  id="datasetId"
                  name="datasetId"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter dataset id"
                  value={formData.datasetId}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="reportUrl" className="form-label rr-label">
                  Report URL
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  id="reportUrl"
                  name="reportUrl"
                  type="text"
                  className="form-control rr-input"
                  placeholder="https://app.powerbi.com/reportEmbed?reportId=...&groupId=..."
                  value={formData.reportUrl}
                  disabled
                  readOnly
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="rlsEnabled" className="form-label rr-label">
                  RLS Enabled
                </label>
                <div className="rr-toggle" role="radiogroup" aria-label="RLS Enabled">
                  <label
                    className={`rr-toggleOption ${
                      formData.rlsEnabled === "true" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="rlsEnabled"
                      value="true"
                      checked={formData.rlsEnabled === "true"}
                      onChange={handleChange}
                    />
                    True
                  </label>
                  <label
                    className={`rr-toggleOption ${
                      formData.rlsEnabled === "false" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="rlsEnabled"
                      value="false"
                      checked={formData.rlsEnabled === "false"}
                      onChange={handleChange}
                    />
                    False
                  </label>
                </div>
              </div>

              <div className="col-12">
                <label
                  htmlFor="reportDescription"
                  className="form-label rr-label"
                >
                  Report Description
                </label>
                <textarea
                  id="reportDescription"
                  name="reportDescription"
                  className="form-control rr-input rr-textarea"
                  rows="4"
                  placeholder="Enter report description"
                  value={formData.reportDescription}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="d-flex gap-2 mt-4 rr-actions">
              <button type="submit" className="btn rr-btnPrimary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Submitting...
                  </>
                ) : editReportData ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </button>
              <button
                type="button"
                className="btn rr-btnGhost"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportRegistrationsFrom;
